import {
  buildCalendar,
  cardOfTheDay,
  familiarGreeting,
  moonSignAt,
  moonStateAt,
  nextPhaseEvent,
  nextSabbat,
  phaseLabel,
  SABBAT_NAMES,
  startMs,
} from "@hexling/core";
import { EmptyState, Moon, Spinner } from "@hexling/ui";
import { Link } from "@tanstack/react-router";
import { useContent, useDeviceId, useMe, useProfile } from "../../data/AppData";
import { eventDate, formatDay, localDayKey, relativeDays } from "../../lib/dates";

const cap = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

export function TodayPage({ now = new Date() }: { now?: Date }) {
  const content = useContent();
  const profile = useProfile().data;
  const me = useMe().data;
  const device = useDeviceId().data;

  if (content.isPending || !profile) return <Spinner label="Opening your grimoire" />;
  const index = content.data;
  const s = profile.settings;

  const greeting = familiarGreeting({
    familiar: profile.familiar,
    witchName: me?.user?.name?.split(" ")[0],
    hour: now.getHours(),
    moonPhase: undefined,
  });

  if (!index) {
    return (
      <>
        <p className="greeting">{greeting}</p>
        <EmptyState glyph="🌒" title="Your library is on its way">
          Hexling downloads its herbs, crystals and moon tables the first time you're online. After
          that, everything works offline.
        </EmptyState>
      </>
    );
  }

  const moon = s.features.lunar ? moonStateAt(now, index.lunar) : null;
  const fullMoon = s.features.lunar ? nextPhaseEvent(now, index.lunar, "full_moon") : undefined;
  const newMoon = s.features.lunar ? nextPhaseEvent(now, index.lunar, "new_moon") : undefined;
  const sabbat = s.features.sabbats ? nextSabbat(index.sabbatDates, s, now) : undefined;
  const sabbatItem = sabbat && index.get("sabbat", sabbat.sabbat.sabbat_slug);
  const deck = s.features.tarot ? index.list("tarot_card") : [];
  const draw =
    deck.length && device
      ? cardOfTheDay(me?.user?.id ?? device, localDayKey(now), deck.length)
      : null;
  const card = draw ? deck[draw.index] : undefined;
  const until = new Date(now.getTime() + 14 * 86_400_000);
  const upcoming = buildCalendar(
    {
      lunar: index.lunar,
      sabbats: index.sabbatDates,
      eclipses: index.eclipses,
      retrogrades: index.retrogrades,
    },
    s,
    now,
    until,
  )
    .filter((e) => startMs(e.start) >= now.getTime() - 86_400_000)
    .slice(0, 5);

  const moonGreeting = familiarGreeting({
    familiar: profile.familiar,
    witchName: me?.user?.name?.split(" ")[0],
    hour: now.getHours(),
    moonPhase: moon?.phase,
  });

  return (
    <div className="today">
      {moon ? (
        <section className="almanac" aria-labelledby="moon-heading">
          <div className="almanac__moon">
            <Moon
              age={moon.age}
              hemisphere={s.hemisphere}
              size={176}
              label={`${phaseLabel(moon.phase)}, ${Math.round(moon.illumination * 100)}% illuminated`}
            />
          </div>
          <div className="almanac__entry">
            <p className="almanac__date">
              {formatDay(now, { weekday: "long", month: "long", day: "numeric" })}
            </p>
            <h1 id="moon-heading">
              {phaseLabel(moon.phase)} in {cap(moonSignAt(now))}
            </h1>
            <p className="almanac__lines">
              {Math.round(moon.illumination * 100)}% lit{moon.isDarkMoon ? ", the dark moon" : ""}.
              {fullMoon && moon.phase !== "full_moon" && (
                <>
                  {" "}
                  {fullMoon.traditional_name ?? "Full moon"}{" "}
                  {relativeDays(new Date(fullMoon.at_utc), now)}.
                </>
              )}
              {newMoon && moon.phase === "full_moon" && (
                <> New moon {relativeDays(new Date(newMoon.at_utc), now)}.</>
              )}
            </p>
            <Link
              to="/library/$category/$slug"
              params={{ category: "moon_phase", slug: moon.phase }}
            >
              Working with the {phaseLabel(moon.phase).toLowerCase()}
            </Link>
          </div>
        </section>
      ) : (
        <h1 className="almanac__date">
          {formatDay(now, { weekday: "long", month: "long", day: "numeric" })}
        </h1>
      )}

      <p className="greeting">{moon ? moonGreeting : greeting}</p>

      <div className="today__grid">
        {sabbat && sabbatItem && (
          <section className="hx-panel today__sabbat" aria-labelledby="sabbat-heading">
            <h2 id="sabbat-heading">
              <Link
                to="/library/$category/$slug"
                params={{ category: "sabbat", slug: sabbatItem.slug }}
              >
                {SABBAT_NAMES[sabbatItem.slug] ?? sabbatItem.name}
              </Link>
            </h2>
            <p className="today__count">
              {sabbat.daysAway === 0
                ? "Today"
                : sabbat.daysAway === 1
                  ? "Tomorrow"
                  : `In ${sabbat.daysAway} days`}
            </p>
            <p className="hx-muted">{sabbatItem.summary}</p>
          </section>
        )}

        {card && draw && (
          <section className="hx-panel today__card" aria-labelledby="card-heading">
            <h2 id="card-heading">Card of the day</h2>
            <p className="tarot-name">
              <Link
                to="/library/$category/$slug"
                params={{ category: "tarot_card", slug: card.slug }}
              >
                {card.name}
              </Link>
              {draw.reversed && <span className="hx-muted"> (reversed)</span>}
            </p>
            <p>
              {String(
                (draw.reversed ? card.data.meaning_reversed : card.data.meaning_upright) ?? "",
              )}
            </p>
            <Link to="/tarot">Draw a spread</Link>
          </section>
        )}
      </div>

      {upcoming.length > 0 && (
        <section aria-labelledby="soon-heading" className="today__soon">
          <h2 id="soon-heading">Coming up</h2>
          <ul className="event-list">
            {upcoming.map((e) => (
              <li key={e.id} className={`event event--${e.kind}`}>
                <time dateTime={e.start}>
                  {formatDay(eventDate(e.start), { month: "short", day: "numeric" })}
                </time>
                <span>{e.title}</span>
              </li>
            ))}
          </ul>
          <Link to="/calendar">Open the calendar</Link>
        </section>
      )}
    </div>
  );
}
